import * as React from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";

import { ActionEyebrow, ActionIconBadge, cn } from "@ando/ui";

import {
  useProposalsStore,
} from "@/features/sources/state/proposalsStore";
import { useAllPendingActions, activeActionApi } from "../hooks/useActiveAction";
import { resolveTabForActionType } from "../actionTypeToTab";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import type { ActiveAction } from "../state/activeActionStore";
import { iconForActionType } from "../utils/actionTypeIcon";

/**
 * Action types whose proposals also exist as a tracked-change chip in
 * the active editor (via ``useProposalsStore``). The notification chip
 * is suppressed for those when the chip is already applied — the user
 * sees the diff in-editor, no banner needed (May 23, 2026 anti-double-
 * confirm refinement).
 */
const DOC_DRAWER_ACTION_TYPES = new Set([
  "document_edit",
  "document_fork_edit",
]);

/**
 * ActionFormSurface — slim "pending proposals" index chip stack.
 *
 * ── Pre-May-24-2026 behaviour ──────────────────────────────────────
 * Rendered the full :class:`ActionDrawer` card ABOVE the routed hub
 * content (squashed mini-card in cima alla work area). UX audit
 * flagged the layout as "non si capisce cosa stai facendo" — two
 * focal points (drawer + hub list) competed for attention and the
 * card's compact chrome looked nothing like the calendar / inbox
 * detail panes.
 *
 * ── Current behaviour (May 30, 2026) ───────────────────────────────
 * Every fresh ``action_proposal`` now AUTO-OPENS its focused work-area
 * surface the instant it lands (``useActionRouter`` navigates to
 * ``?proposal=<id>`` unconditionally — no ``currentFocus`` gate). So
 * this strip is NO LONGER the entry point: it is a purely informational
 * INDEX of the OTHER pending proposals the user hasn't decided yet
 * (e.g. an email proposal that arrived earlier while a newer SF
 * proposal has since taken over the canvas). Clicking a chip returns
 * focus to that proposal; the X dismisses it. The proposal currently
 * occupying the work area (``?proposal=<this-id>`` on the URL) is
 * filtered out — its surface IS already open, a chip would be a
 * redundant double-anchor.
 *
 * Net: the user never has to click a chip to SEE a proposal — the
 * latest one is always already open. The strip only matters when 2+
 * proposals are in flight and the user wants to hop back to a prior one.
 */
export const ActionFormSurface: React.FC = () => {
  // ALL pending proposals (cross-tab). The chip strip is the user's
  // permanent index of in-flight HITL work — clicking a chip switches
  // tab + opens the focused view. Filtering by current tab (pre-May 25,
  // 2026 behaviour) hid proposals once the user navigated away, forcing
  // them to scroll chat history to find the chip — explicit user
  // complaint addressed.
  const actions = useAllPendingActions();
  const proposalsByDoc = useProposalsStore((s) => s.byDoc);
  const location = useLocation();

  const openProposalId =
    typeof (location.search as { proposal?: unknown }).proposal === "string"
      ? ((location.search as { proposal?: string }).proposal as string)
      : null;

  const visibleActions = React.useMemo(() => {
    if (actions.length === 0) return actions;
    const appliedChipIds = new Set<string>();
    for (const list of Object.values(proposalsByDoc)) {
      for (const p of list) {
        if (p.applied) appliedChipIds.add(p.proposal_id);
      }
    }
    return actions.filter((a) => {
      // Hide the chip for the proposal that's already open in the
      // focused view — avoids a stale "in attesa" sticker next
      // to the active card.
      if (a.id === openProposalId) return false;
      // ``document_edit`` / ``document_fork_edit`` ALWAYS route to the editor
      // (``?focus=<docId>``), where the loader → in-slide redline rail (or, if
      // the diff never correlates, ArtifactPane's bounded fallback notice) owns
      // the review. The top "PROPOSTA IN ATTESA" chip is NEVER the surface for
      // them — suppress it unconditionally, from the start, so it doesn't flash
      // above the editor during "Preparo l'anteprima…" (June 19, 2026). Earlier
      // we only suppressed once the diff had arrived, which left the chip
      // visible during the whole loading window.
      if (DOC_DRAWER_ACTION_TYPES.has(a.actionType)) return false;
      return !appliedChipIds.has(a.id);
    });
  }, [actions, proposalsByDoc, openProposalId]);

  if (visibleActions.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5 border-hairline border-b bg-card/60 px-4 py-2 backdrop-blur-sm">
      {visibleActions.map((a) => (
        <NotificationChip key={a.id} action={a} />
      ))}
    </div>
  );
};
ActionFormSurface.displayName = "ActionFormSurface";

// ── Single notification chip ─────────────────────────────────────────────

const NotificationChip: React.FC<{ action: ActiveAction }> = ({ action }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const Icon = iconForActionType(action.actionType);

  // Resolve the action's destination tab so the chip jumps to the
  // RIGHT workspace tab (e.g. ``document_create`` → ``/workspace/documents``)
  // even if the user is currently on a different tab. Without this the
  // chip stayed on the current path with ``?proposal=`` set, which
  // mounts the focused view but doesn't visually update the tab strip
  // — confusing when the user expects "Documenti" to flash.
  const destTab = resolveTabForActionType(action.actionType);
  const destDef = getWorkspaceTabDef(destTab);

  const onOpen = React.useCallback(() => {
    if (!destDef) return;
    void navigate({
      to: destDef.route,
      search: { proposal: action.id },
    });
  }, [action.id, destDef, navigate]);

  const onDismiss = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      activeActionApi.remove(action.id);
    },
    [action.id],
  );

  const openLabel = destDef
    ? t`Torna alla proposta: ${action.title} su ${destDef.label}`
    : t`Torna alla proposta: ${action.title}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-md border border-hairline bg-card px-3 py-2 text-left",
        "transition-[border-color,background-color,box-shadow] duration-200 ease-smooth",
        "hover:border-ando-orange/40 hover:bg-ando-orange-soft/30 hover:shadow-[0_2px_8px_-3px_rgba(255,122,26,0.18)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
      )}
      aria-label={openLabel}
    >
      <ActionIconBadge icon={<Icon />} size="sm" tone="accent" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <ActionEyebrow
          label={t`Proposta in attesa`}
          suffix={destDef?.label ?? action.vendorLabel}
        />
        <span className="truncate text-[12.5px] text-ink-1 leading-snug">
          {action.title}
        </span>
      </span>
      <span
        aria-hidden
        className="hidden shrink-0 font-medium text-[11px] text-ando-orange transition-transform duration-150 ease-out group-hover:inline group-hover:translate-x-0.5"
      >
        <Trans>Torna →</Trans>
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t`Rifiuta proposta`}
        className="ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-black/5 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
      >
        <X className="h-3 w-3" strokeWidth={2} aria-hidden />
      </button>
    </button>
  );
};
NotificationChip.displayName = "NotificationChip";

